import React, { useState } from 'react';
import { CookieManager } from 'react-cookie-manager';
import PFSBody from './components/PFSBody/PFSBody';
import PFSFooter from './components/PFSFooter/PFSFooter';
import PFSHeader from './components/PFSHeader/PFSHeader';
import { useGoogleTracking } from './hooks/useGoogleTracking';

import { Provider, ErrorBoundary } from '@rollbar/react';
import BreadcrumbContext from './contexts/BreadcrumbContext';

const rollbarConfig = {
  accessToken: process.env.REACT_APP_ROLLBAR_KEY,
  environment: process.env.REACT_APP_ROLLBAR_ENV,
  // React 18 attaches __reactFiber$* properties to DOM elements, which
  // contain circular refs (DOMNode → fiber → stateNode → DOMNode).  When
  // Rollbar's click telemetry captures a DOM element (e.g. an anchor click
  // from react-cookie-manager's banner), JSON.stringify hits the circular
  // structure and crashes.  The transform hook strips these before the
  // final serialization so the actual error context still gets reported.
  transform: function (payload) {
    const seen = new WeakSet();
    payload.data = JSON.parse(
      JSON.stringify(payload.data, (key, val) => {
        if (typeof val === 'object' && val !== null) {
          if (seen.has(val)) return '[Circular]';
          seen.add(val);
        }
        return val;
      })
    );
  },
};

// AppContent is rendered *inside* <CookieManager>, so useCookieConsent
// (called by useGoogleTracking) has access to the consent context.
function AppContent() {
  useGoogleTracking();
  const [breadcrumbItems, setBreadcrumbItems] = useState([]);

  return (
    <Provider config={rollbarConfig}>
      <ErrorBoundary>
        <BreadcrumbContext.Provider value={{
          breadcrumbItems,
          setBreadcrumbItems
        }}>
          <PFSHeader />
          <PFSBody />
          <PFSFooter />
        </BreadcrumbContext.Provider>
      </ErrorBoundary>
    </Provider>
  );
}

function App() {
  return (
    <CookieManager
      googleConsentMode={true}
      privacyPolicyUrl="/privacy"
      displayType="banner"
      enableFloatingButton={true}
      translations={{
        title: 'Your Privacy, Our Priority',
        message:
          'We use essential cookies to keep things running, and optional ones to understand how our site performs.',
        buttonText: 'Accept All',
        declineButtonText: 'Essential Only',
        manageButtonText: 'Customize',
        privacyPolicyText: 'Privacy Policy',
      }}
      // react-cookie-manager hardcodes google.com in all three built-in
      // tracker domain lists (Analytics/Advertising/Social).  This blocks
      // reCAPTCHA fetch calls (google.com/recaptcha/api2/…) when consent is
      // declined.  There is no per-domain exemption API — the only supported
      // way to allow reCAPTCHA through is to disable automatic blocking
      // entirely.  CIPA compliance is still enforced by app-level gating:
      //   • useGoogleTracking.js → gtag only loads after Analytics consent
      //   • PFSDiamondBarMap.js → Maps iframe only on hasConsent
      //   • Fonts → migrated to npm (no CDN)
      // GCM consent signals continue to push via googleConsentMode={true}.
      disableAutomaticBlocking={true}
      onAccept={() => {
        // GA gtag config fires on next useGoogleTracking route change
        window.dataLayer?.push({ event: 'pfs_consent_granted' });
      }}
      onDecline={() => {
        // Explicitly push denied state so GCM is unambiguous even if defaults
        // were somehow overridden elsewhere.
        window.gtag?.('consent', 'update', {
          analytics_storage: 'denied',
          ad_storage: 'denied',
          ad_user_data: 'denied',
          ad_personalization: 'denied',
          personalization_storage: 'denied',
        });
      }}
      onManage={(preferences) => {
        // Granular consent — map each category to its GCM signal.
        if (!preferences) return;
        window.gtag?.('consent', 'update', {
          analytics_storage: preferences.Analytics ? 'granted' : 'denied',
          ad_storage: preferences.Advertising ? 'granted' : 'denied',
          ad_user_data: preferences.Advertising ? 'granted' : 'denied',
          ad_personalization: preferences.Advertising ? 'granted' : 'denied',
          personalization_storage: preferences.Social ? 'granted' : 'denied',
        });
      }}
      onConsentLoaded={(consent) => {
        // Fires once on mount for returning visitors who already gave consent.
        // Without this, GA would never fire for users who consented on a prior visit
        // because onAccept only fires during an active interaction.
        if (consent?.Analytics?.consented) {
          window.dataLayer?.push({ event: 'pfs_consent_granted' });
        }
      }}
    >
      <AppContent />
    </CookieManager>
  );
}

export default App;